// Globals
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import { FormItem } from '@/components';
import { EditIcon } from '@/icons';
import { Button, Input, InputNumber } from '@/ui';
import {
  StyledButtons,
  StyledForm,
  StyledInputWrapper,
  StyledLabel,
  StyledText,
  StyledWrapper,
} from './EditableInputStyles';

// Helpers
import { MAX_NUMBER_LENGTH } from '@/constants/common';
import { useBoolean } from '@/hooks';
import { TFormValidatorRule } from '@/types';

type TEditableInputProps<A = string> = {
  label?: string;
  placeholder?: string;
  isLoading?: boolean;
  initialValues?: Record<string, A | undefined>;
  name?: string | number;
  disabled?: boolean;
  rules?: TFormValidatorRule[];
  onChange?: (data: Record<string, A>) => void;
  type?: 'text' | 'number';
  size?: 'large' | 'small' | 'middle';
  text?: ReactNode;
};

const EditableInput = <A,>({
  label,
  onChange,
  name,
  rules,
  placeholder,
  isLoading,
  disabled,
  initialValues,
  size = 'middle',
  type = 'text',
  text,
}: TEditableInputProps<A>) => {
  const { value: isEdit, setTrue, setFalse } = useBoolean();
  const { t } = useTranslation('settings');

  const handleSubmit = async (values: Record<string, any>) => {
    onChange?.(values);
    setFalse();
  };

  return (
    <StyledWrapper>
      <StyledInputWrapper>
        {isEdit && !disabled ? (
          <StyledForm onFinish={handleSubmit} initialValues={initialValues}>
            <FormItem name={name} rules={rules} label={label}>
              {type === 'number' ? (
                <InputNumber size={size} placeholder={placeholder} maxLength={MAX_NUMBER_LENGTH} />
              ) : (
                <Input size={size} placeholder={placeholder} />
              )}
            </FormItem>
            <StyledButtons>
              <Button htmlType="submit" type="primary" shape="round" block size="small" loading={isLoading}>
                {t('save_button')}
              </Button>
              <Button size="small" htmlType="button" shape="round" danger block onClick={setFalse}>
                {t('cancel_button')}
              </Button>
            </StyledButtons>
          </StyledForm>
        ) : (
          <>
            <StyledLabel>{label}</StyledLabel>
            <StyledText>{text ?? (initialValues?.[name ?? ''] as string) ?? '-'}</StyledText>
          </>
        )}
      </StyledInputWrapper>
      {!isEdit && !disabled && <Button ghost type="dashed" icon={<EditIcon />} onClick={setTrue} />}
    </StyledWrapper>
  );
};

export default EditableInput;
